import { useState } from 'react';
import { Avatar, Button, Card, Dropdown, Flex, Tag, theme, Tooltip } from 'antd';
import { FiEdit2, FiMoreVertical, FiTrash2 } from 'react-icons/fi';
import styled from 'styled-components';

import fallbackImage from '@assets/fallbackImage.jpg';
import lightbulbTreeIcon from '@assets/lightbulb_tree.png';
import verifiedIcon from '@assets/biodrone_verified.svg';
import BaseText from '@components/typography/BaseText';
import { useThumbnailUrl } from '@hooks/useThumbnailUrl';
import { PROJECT_TYPE_LABELS } from '@utils/constants';

import {
	canTrainChild,
	displayedAliases,
	hasAnnotationCount,
	isSystemNode,
	minAnnotationsOf,
} from '../objectTree';

// System objects are Biodrone-maintained and read-only, so they sit back visually.
const SYSTEM_OPACITY = 0.72;
const ALIAS_OPACITY = 0.7;

// The PNG is used as a mask so the icon takes the accent colour.
const TrainReadyIcon = styled.span`
	width: ${({ $size }) => $size}px;
	height: ${({ $size }) => $size}px;
	flex: none;
	background: ${({ $color }) => $color};
	mask: url(${lightbulbTreeIcon}) center / contain no-repeat;
`;

const TrainReadyPill = ({ annotations }) => {
	const { token } = theme.useToken();

	return (
		<Tooltip title="Enough annotated examples — you can add a child object here">
			<Flex
				role="img"
				aria-label={`${annotations} annotations, ready to train a child object`}
				align="center"
				gap={token.sizeXXS}>
				<TrainReadyIcon aria-hidden $size={token.sizeMD} $color={token.colorSuccess} />
				<BaseText size="sm" strong type="success">
					{annotations}
				</BaseText>
			</Flex>
		</Tooltip>
	);
};

// Every card carries the pill: the count against the threshold while short of it,
// the green "ready" mark once the node has enough examples to train a child from.
// Before the summary has loaded, or for a node it does not list, the count reads 0.
const AnnotationPill = ({ object }) => {
	const required = minAnnotationsOf(object);
	const annotations = hasAnnotationCount(object) ? object.annotations : 0;

	if (canTrainChild(object)) return <TrainReadyPill annotations={annotations} />;

	return (
		<Tooltip title={`Needs ${required} annotations before a child object can be trained`}>
			<Tag
				role="img"
				aria-label={`${annotations} of ${required} annotations needed to train a child object`}
				color="warning"
				variant="outlined">
				{annotations}/{required}
			</Tag>
		</Tooltip>
	);
};

const ObjectTreeCard = ({ object, mode, selected = false, onEdit, onDelete }) => {
	const { token } = theme.useToken();

	const thumbnailUrl = useThumbnailUrl(object.thumbnail);
	// Keyed to the URL, so a refreshed or changed thumbnail gets another chance to load.
	const [failedUrl, setFailedUrl] = useState(null);
	const thumbnailFailed = failedUrl !== null && failedUrl === thumbnailUrl;

	const isSystem = isSystemNode(object);
	const aliases = displayedAliases(object).join(' · ') || null;
	const projectType = PROJECT_TYPE_LABELS[mode] ?? 'this project type';

	// The row itself selects the object, so neither the menu trigger nor a menu
	// item may bubble to it. Delete stays a no-op until Objects wires a handler.
	const actionMenu = {
		items: [
			{ key: 'edit', label: 'Edit', icon: <FiEdit2 aria-hidden /> },
			{ key: 'delete', label: 'Delete', icon: <FiTrash2 aria-hidden />, danger: true },
		],
		onClick: ({ key, domEvent }) => {
			domEvent.stopPropagation();
			(key === 'edit' ? onEdit : onDelete)?.(object);
		},
	};

	return (
		<Card
			size="small"
			style={{
				borderColor: selected ? token.colorPrimary : undefined,
				opacity: isSystem ? SYSTEM_OPACITY : 1,
			}}>
			<Flex align="center" gap="small">
				<Avatar
					shape="square"
					alt=""
					size={token.sizeXXL}
					src={thumbnailFailed ? fallbackImage : (thumbnailUrl ?? fallbackImage)}
					onError={() => {
						setFailedUrl(thumbnailUrl);
						return false;
					}}
					style={{ flex: 'none' }}
				/>

				<Flex vertical flex={1} style={{ minWidth: 0 }}>
					<Flex align="center" gap="small" style={{ minWidth: 0 }}>
						<BaseText strong ellipsis type={isSystem ? 'secondary' : undefined}>
							{object.name}
						</BaseText>

						{isSystem ? (
							<Tooltip title="Biodrone Verified">
								<img
									src={verifiedIcon}
									alt="Biodrone Verified"
									width={token.sizeMS}
									height={token.sizeMS}
									style={{ flex: 'none' }}
								/>
							</Tooltip>
						) : (
							<Tag color="success" variant="filled">
								Custom
							</Tag>
						)}

						{/* Informational, not disabling: the object is still manageable, it just
						    has no model bound for the selected project type. */}
						{!object.is_available && (
							<Tooltip title={`No model is bound for ${projectType} yet`}>
								<Tag variant="outlined">No model</Tag>
							</Tooltip>
						)}
					</Flex>

					{object.description && (
						<BaseText size="sm" type="secondary" ellipsis>
							{object.description}
						</BaseText>
					)}

					{aliases && (
						<BaseText
							size="sm"
							type="success"
							ellipsis
							style={{ opacity: ALIAS_OPACITY }}>
							{aliases}
						</BaseText>
					)}
				</Flex>

				<AnnotationPill object={object} />

				{/* Every non-system object, for members and admins alike. TODO: split them
				    once the app has an authorization layer — the backend may limit org
				    edits to admins. */}
				{!isSystem && (
					// The Dropdown listens on the button itself, so the stop sits one level
					// up: stopping it on the button would keep the menu from ever opening.
					<Flex style={{ flex: 'none' }} onClick={event => event.stopPropagation()}>
						<Dropdown menu={actionMenu} trigger={['click']} placement="bottomRight">
							<Button
								type="text"
								size="small"
								icon={<FiMoreVertical />}
								aria-label={`Actions for ${object.name}`}
							/>
						</Dropdown>
					</Flex>
				)}
			</Flex>
		</Card>
	);
};

export default ObjectTreeCard;
